import * as React from 'react';

import { Box, Icon, OnClick } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';

import {
  StoreCardActions,
  StoreCardCaption,
  StoreCardDetails,
  StoreCardError,
  StoreCardSeperator,
} from './components';
import { CardButton, FadeIn, HalalContainer, PrimaryText } from './store-card.styled';

type StoreDetails = {
  body?: string;
  title?: string | null;
  distanceText?: string;
  errorMessage?: string;
  isHalal?: boolean;
  inStoreLoyalty?: boolean;
  mobileLoyalty?: boolean;
} & (
  | {
      openText: string;
      closedText?: never;
    }
  | {
      openText?: never;
      closedText: string;
    }
);

interface StoreCardViewProps {
  isFavorite?: boolean;
  isUpdatingFavorites?: boolean;
  onCardClick?: OnClick;
  onDiagnosticsClick?: OnClick;
  onFavoriteClick?: OnClick;
  storeDetails: StoreDetails;
}

export const StoreCardView: React.VFC<StoreCardViewProps> = ({
  isFavorite = false,
  isUpdatingFavorites = false,
  onCardClick,
  onDiagnosticsClick,
  onFavoriteClick,
  storeDetails,
}) => {
  const { formatMessage } = useIntl();
  return (
    <FadeIn>
      <Box
        background={Styles.color.cardBackground}
        padding="0.5rem 0.75rem"
        justify="space-between"
        align="center"
        position="relative"
        overflow="hidden"
        minHeight="96px"
      >
        {onCardClick && (
          <CardButton
            aria-label={`
              ${storeDetails.title?.toLowerCase()}. ${formatMessage({ id: 'openAccordion' })}
            `}
            onClick={onCardClick}
          />
        )}

        <StoreCardDetails
          title={storeDetails.title}
          caption={
            <StoreCardCaption
              prefix={storeDetails.distanceText}
              suffix={
                <span>
                  {storeDetails.isHalal && (
                    <>
                      <HalalContainer>
                        <Icon
                          icon="halal"
                          color="icon-default"
                          title={formatMessage({ id: 'isHalalTitle' })}
                        />
                      </HalalContainer>
                      <span role="separator"> &bull; </span>
                    </>
                  )}
                  {storeDetails.openText || <PrimaryText>{storeDetails.closedText}</PrimaryText>}
                </span>
              }
            />
          }
          body={storeDetails.body}
          errorMessage={
            storeDetails.errorMessage ? (
              <StoreCardError message={storeDetails.errorMessage} />
            ) : null
          }
          inStoreLoyalty={storeDetails.inStoreLoyalty}
          mobileLoyalty={storeDetails.mobileLoyalty}
        />

        <StoreCardSeperator />

        <Box minWidth="fit-content" width="fit-content" zIndex="1">
          <StoreCardActions
            favoriteIcon={isFavorite ? 'favoriteFill' : 'favorite'}
            onDiagnosticsClick={onDiagnosticsClick}
            onFavoritesClick={onFavoriteClick}
            isUpdatingFavorites={isUpdatingFavorites}
          />
        </Box>
      </Box>
    </FadeIn>
  );
};
